import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'

import { appListenerMiddleware } from 'reduxTyped'
import { IInstagramStory } from 'apps/instagram/models/interfaces'
import { NoNextPageError } from 'apps/performanceAnalytics/store/errors'
import { buildContentStatisticsQuery } from 'apps/performanceAnalytics/store/lib/buildContentStatisticsQuery'
import { isQueryFilterless } from 'apps/performanceAnalytics/store/lib/isQueryFilterless'
import { isStoryExpired } from 'apps/performanceAnalytics/store/lib/isStoryExpired'
import { AnalyticsSelectors } from 'apps/performanceAnalytics/store/selectors'
import { AnalyticsInitialState, AutomationFilter } from 'apps/performanceAnalytics/store/types'
import { AnalyticsApi } from 'shared/api/requests/analytics'
import {
  AnalyticsPeriod,
  CombinedStatistics,
  ContentAnalyticsContentType,
  ContentAnalyticsSortField,
  SortOrder,
} from 'shared/api/requests/analytics/schemas'
import { InstagramApi } from 'shared/api/requests/instagram'
import {
  FlowDescriptor,
  FlowsByMediaId,
  IInstagramPost,
  InstagramStory,
} from 'shared/api/requests/instagram/schemas'
import { makeCreateAsyncAction } from 'shared/lib/redux'

import { ContentFetchQueue } from './lib/contentFetchQueue'

const createAsyncAction = makeCreateAsyncAction({
  scope: 'analytics',
  section: null,
})

const NAMESPACE = 'analytics'

const { actions, reducer } = createSlice({
  name: 'analytics',
  initialState: AnalyticsInitialState,
  reducers: () => ({
    stateReset: () => {
      return AnalyticsInitialState
    },
    fallbackEnabled: (state) => {
      state.contentDetails.useIndividualContentLoading = true
    },
    periodUpdated: (state, action: PayloadAction<AnalyticsPeriod>) => {
      state.query = {
        ...AnalyticsInitialState.query,
        period: action.payload,
      }
      state.contentStatistics.isFilterlessFullyLoaded = false
    },
    contentTypeUpdated: (state, action: PayloadAction<ContentAnalyticsContentType | null>) => {
      state.query.contentType = action.payload
    },
    sortUpdated: (
      state,
      action: PayloadAction<{ sort: ContentAnalyticsSortField; sortOrder: SortOrder }>,
    ) => {
      state.query.sortField = action.payload.sort
      state.query.sortOrder = action.payload.sortOrder
    },
    automationPresenceUpdated: (state, action: PayloadAction<AutomationFilter>) => {
      state.query.automation = action.payload
    },
  }),
  extraReducers: (builder) => {
    builder.addCase(getStatisticsSummaryFx.pending, (state) => {
      state.summary.isLoading = true
    })

    builder.addCase(getStatisticsSummaryFx.fulfilled, (state, action) => {
      state.summary.isLoading = false
      state.summary.isLoaded = true
      state.summary.data = action.payload
    })

    builder.addCase(getStatisticsSummaryFx.rejected, (state) => {
      state.summary.isLoading = false
    })

    builder.addCase(getContentStatisticsFx.pending, (state) => {
      state.contentStatistics.isLoading = true
    })

    builder.addCase(getContentStatisticsFx.fulfilled, (state, action) => {
      state.contentStatistics.isLoading = false
      state.contentStatistics.isLoaded = true

      state.contentStatistics.total = action.payload.total
      state.contentStatistics.list = action.payload.data

      if (action.payload.total > action.payload.data.length) {
        state.contentStatistics.nextPage = 2
        state.contentStatistics.isFilterlessFullyLoaded = false
      } else {
        state.contentStatistics.nextPage = -1
        state.contentStatistics.isFilterlessFullyLoaded = isQueryFilterless(state.query)
      }
    })

    builder.addCase(getContentStatisticsFx.rejected, (state) => {
      state.contentStatistics.isLoading = false
    })

    builder.addCase(getContentStatisticsNextPageFx.pending, (state) => {
      state.contentStatistics.isLoadingNextPage = true
    })

    builder.addCase(getContentStatisticsNextPageFx.fulfilled, (state, action) => {
      state.contentStatistics.isLoadingNextPage = false
      state.contentStatistics.list.push(...action.payload.data)

      if (state.contentStatistics.total > state.contentStatistics.list.length) {
        state.contentStatistics.nextPage += 1
        state.contentStatistics.isFilterlessFullyLoaded = false
      } else {
        state.contentStatistics.nextPage = -1
        state.contentStatistics.isFilterlessFullyLoaded = isQueryFilterless(state.query)
      }
    })

    builder.addCase(getContentStatisticsNextPageFx.rejected, (state) => {
      state.contentStatistics.isLoadingNextPage = false
    })

    builder.addCase(getContentDetailsFx.pending, (state) => {
      state.contentDetails.isLoading = true
    })

    builder.addCase(getContentDetailsFx.fulfilled, (state, action) => {
      state.contentDetails.isLoading = false
      state.contentDetails.isLoaded = true
      state.contentDetails.posts = { ...state.contentDetails.posts, ...action.payload.posts }
      state.contentDetails.stories = { ...state.contentDetails.stories, ...action.payload.stories }
    })

    builder.addCase(getContentDetailsFx.rejected, (state) => {
      state.contentDetails.isLoading = false
    })

    builder.addCase(loadContentDetailItemFx.pending, (state, action) => {
      state.contentDetails.loadingIds[action.meta.arg.id] = true
    })

    builder.addCase(loadContentDetailItemFx.fulfilled, (state, action) => {
      const { id } = action.meta.arg
      delete state.contentDetails.loadingIds[id]

      if ('post' in action.payload) {
        state.contentDetails.posts[id] = action.payload.post
      }

      if ('story' in action.payload && action.payload.story) {
        state.contentDetails.stories[id] = action.payload.story
      }
    })

    builder.addCase(loadContentDetailItemFx.rejected, (state, action) => {
      const { id } = action.meta.arg
      delete state.contentDetails.loadingIds[id]
      state.contentDetails.failedIds[id] = true
    })

    builder.addCase(getContentFlowsFx.pending, (state) => {
      state.contentFlows.isLoading = true
    })

    builder.addCase(getContentFlowsFx.fulfilled, (state, action) => {
      state.contentFlows.isLoading = false
      state.contentFlows.isLoaded = true
      state.contentFlows.flows = { ...state.contentFlows.flows, ...action.payload }
    })

    builder.addCase(getContentFlowsFx.rejected, (state) => {
      state.contentFlows.isLoading = false
    })
  },
})

const getStatisticsSummaryFx = createAsyncAction(
  `${NAMESPACE}/getStatisticsSummaryFx`,
  async (_, { getState }) => {
    const { period } = AnalyticsSelectors.getQuery(getState())

    const { data } = await AnalyticsApi.getSummary({
      query: {
        period,
      },
    })

    return data.data
  },
)

const PAGE_SIZE = 25

const getContentStatisticsFx = createAsyncAction(
  `${NAMESPACE}/getContentStatisticsFx`,
  async (_, { getState }) => {
    const { data } = await AnalyticsApi.getContentStatistics({
      query: {
        page: 1,
        page_size: PAGE_SIZE,
        ...buildContentStatisticsQuery(AnalyticsSelectors.getQuery(getState())),
      },
    })

    return data
  },
)

const getContentStatisticsNextPageFx = createAsyncAction(
  `${NAMESPACE}/getContentStatisticsNextPageFx`,
  async (_, { getState }) => {
    const state = getState()

    if (!AnalyticsSelectors.getIsNextPageAvailable(state)) {
      throw new NoNextPageError()
    }

    const { contentStatistics } = AnalyticsSelectors.getState(state)

    const { data } = await AnalyticsApi.getContentStatistics({
      query: {
        page_size: PAGE_SIZE,
        page: contentStatistics.nextPage,
        ...buildContentStatisticsQuery(AnalyticsSelectors.getQuery(state)),
      },
    })

    return data
  },
)

const fetchPostFlows = async (ids: string[]): Promise<FlowsByMediaId> => {
  if (ids.length === 0) return {}
  const { data } = await InstagramApi.getPostsFlows({ query: { post_ids: ids } })
  return data.flows
}

const fetchStoryFlows = async (ids: string[]): Promise<FlowsByMediaId> => {
  if (ids.length === 0) return {}
  const { data } = await InstagramApi.getStoriesFlows({ query: { story_ids: ids } })
  return data.flows
}

const getContentFlowsFx = createAsyncAction(
  `${NAMESPACE}/getContentFlowsFx`,
  async (items: CombinedStatistics[], { getState }): Promise<Record<string, FlowDescriptor[]>> => {
    const { flows: cachedFlows } = AnalyticsSelectors.getContentFlows(getState())

    const { postIds, storyIds } = items.reduce<{
      postIds: Set<string>
      storyIds: Set<string>
    }>(
      (result, { content: { post_type, igmedia_id }, automation }) => {
        if (automation || igmedia_id in cachedFlows) {
          return result
        }

        if (post_type === ContentAnalyticsContentType.STORY) {
          result.storyIds.add(igmedia_id)
        } else {
          result.postIds.add(igmedia_id)
        }

        return result
      },
      { postIds: new Set(), storyIds: new Set() },
    )

    const [postFlows, storyFlows] = await Promise.all([
      fetchPostFlows([...postIds]),
      fetchStoryFlows([...storyIds]),
    ])

    const allFlows = { ...postFlows, ...storyFlows }

    return [...postIds, ...storyIds].reduce<Record<string, FlowDescriptor[]>>((acc, id) => {
      acc[id] = allFlows[id] ?? []
      return acc
    }, {})
  },
)

const fetchPosts = async (ids: string[]): Promise<Record<string, IInstagramPost>> => {
  if (ids.length === 0) return {}
  const { data } = await InstagramApi.getPostsBatch({ query: { post_ids: ids } })
  return data.posts
}

const fetchStories = async (ids: string[]): Promise<Record<string, IInstagramStory>> => {
  if (ids.length === 0) return {}
  const { data } = await InstagramApi.getStoriesBatch({ query: { story_ids: ids } })
  return data.stories
}

const getContentDetailsFx = createAsyncAction(
  `${NAMESPACE}/getContentDetailsFx`,
  async (
    items: CombinedStatistics[],
    { getState },
  ): Promise<{
    posts: Record<string, IInstagramPost>
    stories: Record<string, IInstagramStory>
  }> => {
    const {
      posts: cachedPosts,
      stories: cachedStories,
      useIndividualContentLoading,
    } = AnalyticsSelectors.getContentDetails(getState())

    if (useIndividualContentLoading) {
      return { posts: {}, stories: {} }
    }

    const { postIds, storyIds } = items.reduce<{ postIds: Set<string>; storyIds: Set<string> }>(
      (acc, { content: { post_type, igmedia_id, published_at } }) => {
        if (post_type === ContentAnalyticsContentType.STORY) {
          if (!cachedStories[igmedia_id] && !isStoryExpired(published_at)) {
            acc.storyIds.add(igmedia_id)
          }

          return acc
        }

        if (!cachedPosts[igmedia_id]) {
          acc.postIds.add(igmedia_id)
        }

        return acc
      },
      { postIds: new Set(), storyIds: new Set() },
    )

    const [posts, stories] = await Promise.all([
      fetchPosts([...postIds]),
      fetchStories([...storyIds]),
    ])

    return { posts, stories }
  },
  {
    onBusinessError: (error, _, { dispatch }) => {
      if (
        InstagramApi.getPostsBatch.isNotSuitableConnectionError(error) ||
        InstagramApi.getStoriesBatch.isNotSuitableConnectionError(error)
      ) {
        error.handle()
        dispatch(actions.fallbackEnabled())
      }
    },
  },
)

const loadContentDetailItemFx = createAsyncAction(
  `${NAMESPACE}/loadContentDetailItemFx`,
  async (arg: {
    id: string
    type: ContentAnalyticsContentType
  }): Promise<{ post: IInstagramPost } | { story: InstagramStory | null }> => {
    if (arg.type === ContentAnalyticsContentType.STORY) {
      const { data } = await InstagramApi.getStory({ path: { storyId: arg.id } })

      return { story: data.story }
    }

    const { data } = await InstagramApi.getPostById({ query: { post_id: arg.id } })

    return { post: data.post }
  },
)

appListenerMiddleware.startListening({
  actionCreator: actions.stateReset,
  effect: () => {
    ContentFetchQueue.resetItemFetchQueue()
  },
})

appListenerMiddleware.startListening({
  actionCreator: actions.periodUpdated,
  effect: (_, { dispatch }) => {
    dispatch(getStatisticsSummaryFx())
    dispatch(getContentStatisticsFx())
  },
})

appListenerMiddleware.startListening({
  actionCreator: getContentStatisticsFx.fulfilled,
  effect: (action, { dispatch }) => {
    if (action.payload.data.length > 0) {
      dispatch(getContentDetailsFx(action.payload.data))
      dispatch(getContentFlowsFx(action.payload.data))
    }
  },
})

appListenerMiddleware.startListening({
  actionCreator: getContentStatisticsNextPageFx.fulfilled,
  effect: (action, { dispatch }) => {
    if (action.payload.data.length > 0) {
      dispatch(getContentDetailsFx(action.payload.data))
      dispatch(getContentFlowsFx(action.payload.data))
    }
  },
})

appListenerMiddleware.startListening({
  matcher: isAnyOf(
    actions.contentTypeUpdated,
    actions.sortUpdated,
    actions.automationPresenceUpdated,
  ),
  effect: (_, { dispatch, getState }) => {
    if (AnalyticsSelectors.getIsFilterlessFullyLoaded(getState())) {
      return
    }

    dispatch(getContentStatisticsFx())
  },
})

export const AnalyticsActions = {
  stateReset: actions.stateReset,
  periodUpdated: actions.periodUpdated,
  contentTypeUpdated: actions.contentTypeUpdated,
  sortUpdated: actions.sortUpdated,
  automationPresenceUpdated: actions.automationPresenceUpdated,

  getContentStatisticsFx,
  getStatisticsSummaryFx,
  getContentStatisticsNextPageFx,
  getContentDetailsFx,
  getContentFlowsFx,
  loadContentDetailItemFx,
}

export const AnalyticsReducer = reducer
